import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Badge,
  CircularProgress,
} from "@mui/material";
import { Notifications } from "@mui/icons-material";
import {
  useGetAllNotificationsQuery,
  useSetNotificationSeenMutation,
  useUserNotificationSubscriptionSubscription,
} from "../../../__generated__/graphql";
import {
  countUnreadNotifications,
  getCurrentUserId,
  isNotificationUnreadForUser,
} from "../../../services/notifications/unread";

// Only http(s) absolute URLs and same-origin relative paths are navigable;
// dangerous schemes (javascript:, data:, vbscript:, file:, ...) and malformed
// strings are rejected.
const SAFE_URL_SCHEMES = new Set(["http:", "https:"]);

function getSafeNotificationLink(link?: string | null): string | null {
  if (!link) return null;
  const trimmed = link.trim();
  if (!trimmed) return null;

  try {
    const resolved = new URL(trimmed, window.location.origin);
    return SAFE_URL_SCHEMES.has(resolved.protocol) ? trimmed : null;
  } catch {
    return null;
  }
}

// Helper function to format time ago
function getTimeAgo(date: Date): string {
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;

  return date.toLocaleDateString();
}

export default function NotificationsBell() {
  const [notificationAnchorEl, setNotificationAnchorEl] =
    useState<HTMLElement | null>(null);
  const [mounted, setMounted] = useState(false);

  const currentUserId = getCurrentUserId();
  const{
    data: notificationsData,
    loading: notificationsLoading,
    refetch: refetchNotifications,
  } = useGetAllNotificationsQuery({
    fetchPolicy: "network-only",
  });
  
  
  const [setNotificationSeenMutation] = useSetNotificationSeenMutation();

  // Subscribe to real-time notifications
  const { data: subscriptionData } =
    useUserNotificationSubscriptionSubscription();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Handle new notifications from subscription
  useEffect(() => {
    if (subscriptionData?.userNotification) {
      refetchNotifications();
    }
  }, [subscriptionData, refetchNotifications]);

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!mounted) return;
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    if (!mounted) return;
    setNotificationAnchorEl(null);
  };

  const markNotificationSeen = (notificationId: string) => {
    setNotificationSeenMutation({ variables: { notificationId } })
      .then(() => refetchNotifications())
      .catch((error) => {
        console.error("Failed to mark notification as seen:", error);
      });
  };

  // Clicking anywhere on the card: mark as seen and, if the notification has
  // a destination, navigate to it the same way the embedded link does.
  const handleCardClick = (notificationId: string, link?: string | null) => {
    markNotificationSeen(notificationId);
    handleNotificationClose();
    const safeLink = getSafeNotificationLink(link);
    if (safeLink) {
      window.open(safeLink, "_blank", "noopener,noreferrer");
    }
  };
// Clicking the embedded link directly: let the anchor's native navigation
// run, but stop the click from also bubbling into handleCardClick so we
// don't open the destination twice or send a duplicate "seen" mutation.
const handleLinkClick = (
  event: React.MouseEvent<HTMLAnchorElement>,
  notificationId: string,
) => {
  event.stopPropagation();
  markNotificationSeen(notificationId);
  handleNotificationClose();
};

// Calculate unread count (per-user; see services/notifications/unread.ts)
const unreadCount = countUnreadNotifications(
  notificationsData?.getAllNotifications || [],
  currentUserId,
);


  // Sort notifications by creation date (newest first)
  const sortedNotifications = [
    ...(notificationsData?.getAllNotifications || []),
  ].sort(
    (a, b) =>
      new Date(b.metadata?.createdAt || 0).getTime() -
      new Date(a.metadata?.createdAt || 0).getTime(),
  );

  if (!mounted) return null;

  return (
    <>
      {/* Notifications Bell Button */}
      <IconButton
        onClick={handleNotificationOpen}
        sx={{
          color: "#6B7280",
          p: { xs: 0.5, sm: 0.75 },
          "&:hover": { backgroundColor: "rgba(107, 114, 128, 0.08)" },
          position: "relative",
        }}
      >
        <Badge
          badgeContent={unreadCount}
          sx={{
            "& .MuiBadge-badge": {
              backgroundColor: unreadCount > 0 ? "#EF4444" : "#D1D5DB",
              color: "#FFFFFF",
              fontSize: { xs: "0.55rem", sm: "0.625rem" },
              minWidth: { xs: 12, sm: 14 },
              height: { xs: 12, sm: 14 },
            },
          }}
        >
          <Notifications sx={{ fontSize: { xs: 14, sm: 16 } }} />
        </Badge>
      </IconButton>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            mt: { xs: 1, md: 1.25 },
            borderRadius: { xs: 1.25, md: 1.5 },
            border: "1px solid #E5E7EB",
            maxWidth: { xs: 280, sm: 360, md: 400 },
            maxHeight: { xs: 400, md: 500 },
            width: { xs: 280, sm: 360, md: 400 },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {notificationsLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 3,
              px: 2,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : sortedNotifications.length === 0 ? (
          <Box
            sx={{
              py: 2,
              px: 2,
              textAlign: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#6B7280",
                fontSize: { xs: "0.75rem", sm: "0.8125rem" },
              }}
            >
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: { xs: 360, md: 450 }, overflowY: "auto" }}>
            {sortedNotifications.map((notification, index) => {
              // Determine whether the current user has seen this notification.
              const isSeen = !isNotificationUnreadForUser(
                notification,
                currentUserId,
              );
              const createdAt = notification.metadata?.createdAt
                ? new Date(notification.metadata.createdAt)
                : null;
              const timeAgo = createdAt ? getTimeAgo(createdAt) : "Recently";
              const safeLink = getSafeNotificationLink(notification.link);

              return (
                <React.Fragment key={notification.id}>
                  <MenuItem
                    onClick={() => handleCardClick(notification.id, safeLink)}
                    aria-label={
                      safeLink
                        ? `${notification.title}. Opens ${safeLink}`
                        : notification.title
                    }
                    sx={{
                      backgroundColor: isSeen
                        ? "#FFFFFF"
                        : "rgba(239, 68, 68, 0.05)",
                      py: { xs: 1.25, sm: 1.5 },
                      px: { xs: 1.5, sm: 2 },
                      borderLeft: isSeen ? "none" : "3px solid #EF4444",
                      cursor: safeLink ? "pointer" : "default",
                      "&:hover": {
                        backgroundColor: isSeen
                          ? "rgba(107, 114, 128, 0.05)"
                          : "rgba(239, 68, 68, 0.1)",
                      },
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      whiteSpace: "normal",
                      minHeight: { xs: 60, sm: 70 },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        width: "100%",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: isSeen ? 400 : 600,
                          color: isSeen ? "#6B7280" : "#1F2937",
                          fontSize: { xs: "0.8rem", sm: "0.8125rem" },
                          flex: 1,
                          wordBreak: "break-word",
                        }}
                      >
                        {notification.title}
                      </Typography>
                      {!isSeen && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: "#EF4444",
                            flexShrink: 0,
                            mt: 0.5,
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: isSeen ? "#9CA3AF" : "#4B5563",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        mt: 0.5,
                        lineHeight: 1.4,
                        wordBreak: "break-word",
                      }}
                    >
                      {notification.body}
                    </Typography>
                    {safeLink && (
                      <Typography
                        component="a"
                        href={safeLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) =>
                          handleLinkClick(event, notification.id)
                        }
                        sx={{
                          color: "#65676b",
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          mt: 0.5,
                          fontWeight: "bold",
                          textDecoration: "none",
                          wordBreak: "break-all",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {safeLink}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#9CA3AF",
                        fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        mt: 0.5,
                      }}
                    >
                      {timeAgo}
                    </Typography>
                  </MenuItem>
                  {index < sortedNotifications.length - 1 && (
                    <Divider sx={{ my: 0 }} />
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </Menu>
    </>
  );
}
